/**
 * Marketplace API Service
 * Handles all marketplace-related API calls aligned with the current API spec.
 * Base URL: https://marketplace-api.partzflow.com
 * Auth: Cookie-based SSO — withCredentials handles everything automatically.
 */

import axios from 'axios';

const MARKETPLACE_API_URL = process.env.REACT_APP_MARKETPLACE_API_URL;

// Axios instance — SSO cookies sent automatically on every request
const marketplaceClient = axios.create({
    baseURL: MARKETPLACE_API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Track per-request retry count for 429 handling
const _retryCount = new WeakMap();

marketplaceClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;

        // 401 — session expired, redirect to login
        if (status === 401) {
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // 429 — rate limited: wait for Retry-After then retry (max 2 times)
        if (status === 429) {
            const config = error.config;
            const retries = _retryCount.get(config) ?? 0;

            if (retries < 2) {
                _retryCount.set(config, retries + 1);

                // Parse Retry-After header (seconds or HTTP date)
                const retryAfter = error.response.headers?.['retry-after'];
                let waitMs = (retries + 1) * 1500; // default: 1.5s, 3s
                if (retryAfter) {
                    const seconds = parseInt(retryAfter, 10);
                    waitMs = isNaN(seconds)
                        ? Math.max(0, new Date(retryAfter) - Date.now())
                        : seconds * 1000;
                }

                await new Promise((resolve) => setTimeout(resolve, waitMs));
                return marketplaceClient(config);
            }

            const err = new Error('Rate limit exceeded. Please wait a moment and try again.');
            err.status = 429;
            return Promise.reject(err);
        }

        return Promise.reject(error);
    }
);

// ============================================
// Auth / Profile
// ============================================

/**
 * GET /marketplace/profile
 * Returns the logged-in user's marketplace profile.
 */
export const getMarketplaceProfile = async () => {
    try {
        const response = await marketplaceClient.get('/marketplace/profile');
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch profile');
    }
};

// ============================================
// Components (product listings)
// ============================================

/**
 * GET /components
 * Browse all components with optional filters.
 *
 * Filters: skip, limit, mpn, manufacturer, category, condition,
 *          min_price, max_price, available_only
 */
export const getComponents = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.skip !== undefined) params.append('skip', filters.skip);
        if (filters.limit !== undefined) params.append('limit', filters.limit);
        if (filters.mpn) params.append('mpn', filters.mpn);
        if (filters.manufacturer) params.append('manufacturer', filters.manufacturer);
        if (filters.category) params.append('category', filters.category);
        if (filters.condition) params.append('condition', filters.condition);
        if (filters.min_price !== undefined) params.append('min_price', filters.min_price);
        if (filters.max_price !== undefined) params.append('max_price', filters.max_price);
        if (filters.available_only !== undefined) params.append('available_only', filters.available_only);
        const response = await marketplaceClient.get(`/components?${params.toString()}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch components');
    }
};

/**
 * GET /components/search?q=...
 * Full-text search across mpn, manufacturer, and description.
 * Minimum 2 characters.
 */
export const searchComponents = async (query, limit = 20) => {
    if (!query || query.trim().length < 2) {
        throw new Error('Search query must be at least 2 characters');
    }
    try {
        const response = await marketplaceClient.get(
            `/components/search?q=${encodeURIComponent(query.trim())}&limit=${Math.min(limit, 100)}`
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to search components');
    }
};

/**
 * GET /components/{id}
 * Fetch a single component by ID.
 */
export const getComponent = async (id) => {
    try {
        const response = await marketplaceClient.get(`/components/${id}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch component');
    }
};

/**
 * GET /components/my
 * Get the current user's own component listings.
 * Optional status filter: 'active' | 'sold_out' | 'inactive'
 */
export const getMyComponents = async (status = null) => {
    try {
        const url = status ? `/components/my?status=${status}` : '/components/my';
        const response = await marketplaceClient.get(url);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch your components');
    }
};

/**
 * POST /components
 * Create a new component listing.
 *
 * Required: listed_quantity, available_quantity, price, condition
 * condition: 'new' | 'used' | 'refurbished'
 */
export const createComponent = async (componentData) => {
    try {
        const response = await marketplaceClient.post('/components', componentData);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to create component');
    }
};

/**
 * PUT /components/{id}
 * Update a component listing (owner only). Send only fields you want to change.
 * status: 'active' | 'sold_out' | 'inactive'
 */
export const updateComponent = async (id, componentData) => {
    try {
        const response = await marketplaceClient.put(`/components/${id}`, componentData);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to update component');
    }
};

/**
 * PATCH /components/{id}/quantity
 * Adjust stock levels (owner only).
 *
 * quantity_change: positive = add, negative = remove
 * update_type: 'listed' | 'available' | 'both'
 */
export const updateComponentQuantity = async (id, quantityChange, updateType = 'available', notes = '') => {
    try {
        const response = await marketplaceClient.patch(`/components/${id}/quantity`, {
            quantity_change: quantityChange,
            update_type: updateType,
            notes,
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to update quantity');
    }
};

/**
 * DELETE /components/{id}
 * Permanently delete a component (owner only).
 */
export const deleteComponent = async (id) => {
    try {
        await marketplaceClient.delete(`/components/${id}`);
        return true;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to delete component');
    }
};

/**
 * GET /components/stats/summary
 * Inventory summary stats for the current user's account.
 */
export const getInventoryStats = async () => {
    try {
        const response = await marketplaceClient.get('/components/stats/summary');
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch inventory stats');
    }
};

// ============================================
// Images
// ============================================

/**
 * POST /upload/image
 * Upload an image file. Returns { url, object_name, size, content_type }.
 * Max 10MB. Types: jpeg, jpg, png, webp, gif.
 *
 * IMPORTANT: Do NOT set Content-Type header — the browser sets it with the
 * multipart boundary automatically when you pass a FormData body.
 */
export const uploadImage = async (file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        // Do NOT set Content-Type — browser sets it with the multipart boundary automatically
        const response = await marketplaceClient.post('/upload/image', formData, {
            headers: { 'Content-Type': undefined },
        });
        return response.data; // { url, object_name, size, content_type }
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to upload image');
    }
};

/**
 * POST /upload/images/batch
 * Upload up to 10 images at once.
 * Returns an array of { url, object_name, ... } objects.
 */
export const uploadImagesBatch = async (files) => {
    try {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        // Do NOT set Content-Type — browser sets it with the multipart boundary automatically
        const response = await marketplaceClient.post('/upload/images/batch', formData, {
            headers: { 'Content-Type': undefined },
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to batch upload images');
    }
};

/**
 * POST /components/{id}/images
 * Attach a previously-uploaded image URL to a component (owner only).
 */
export const attachComponentImage = async (componentId, imageUrl, isPrimary = false, displayOrder = 0) => {
    try {
        const response = await marketplaceClient.post(`/components/${componentId}/images`, {
            listing_id: componentId,
            image_url: imageUrl,
            is_primary: isPrimary,
            display_order: displayOrder,
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to attach image');
    }
};

/**
 * GET /components/{id}/images
 * Public. Returns images ordered by primary first, then display_order.
 */
export const getComponentImages = async (componentId) => {
    try {
        const response = await marketplaceClient.get(`/components/${componentId}/images`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch component images');
    }
};

/**
 * DELETE /components/{component_id}/images/{image_id}
 * Delete an image from a component (owner only).
 */
export const deleteComponentImage = async (componentId, imageId) => {
    try {
        await marketplaceClient.delete(`/components/${componentId}/images/${imageId}`);
        return true;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to delete component image');
    }
};

// ============================================
// Categories
// ============================================

/**
 * GET /categories
 * All top-level categories, or subcategories if parentId is provided.
 */
export const getCategories = async (parentId = null) => {
    try {
        const url = parentId ? `/categories?parent_id=${parentId}` : '/categories';
        const response = await marketplaceClient.get(url);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch categories');
    }
};

// ============================================
// Saved / Favorites
// ============================================

/**
 * POST /saved
 * Save a component (by listing_id which is the component id).
 * Returns 400 if already saved.
 */
export const saveComponent = async (listingId) => {
    try {
        const response = await marketplaceClient.post('/saved', { listing_id: listingId });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to save component');
    }
};

/**
 * GET /saved
 * List the current user's saved components.
 */
export const getSaved = async (skip = 0, limit = 50) => {
    try {
        const response = await marketplaceClient.get(`/saved?skip=${skip}&limit=${limit}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch saved components');
    }
};

/**
 * DELETE /saved/{listing_id}
 * Remove a component from saved.
 */
export const removeSaved = async (listingId) => {
    try {
        await marketplaceClient.delete(`/saved/${listingId}`);
        return true;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to remove from saved');
    }
};

// ============================================
// Messages
// ============================================

/**
 * POST /messages
 * Send a message to a seller about a specific listing.
 * receiver_id = the seller's user_id (from component.user_id).
 */
export const sendMessage = async (listingId, receiverId, message) => {
    try {
        const response = await marketplaceClient.post('/messages', {
            listing_id: listingId,
            receiver_id: receiverId,
            message,
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to send message');
    }
};

/**
 * GET /messages
 * All messages (inbox + sent). Optional filters:
 * - listing_id: filter by component
 * - unread_only: boolean
 */
export const getMessages = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.listing_id) params.append('listing_id', filters.listing_id);
        if (filters.unread_only) params.append('unread_only', filters.unread_only);
        const response = await marketplaceClient.get(`/messages?${params.toString()}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch messages');
    }
};

/**
 * GET /messages/inbox
 * Received messages only.
 */
export const getInbox = async () => {
    try {
        const response = await marketplaceClient.get('/messages/inbox');
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch inbox');
    }
};

/**
 * GET /messages/sent
 * Sent messages only.
 */
export const getSentMessages = async () => {
    try {
        const response = await marketplaceClient.get('/messages/sent');
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch sent messages');
    }
};

/**
 * PATCH /messages/{id}/read
 * Mark a message as read.
 */
export const markMessageRead = async (id) => {
    try {
        const response = await marketplaceClient.patch(`/messages/${id}/read`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to mark message as read');
    }
};

/**
 * DELETE /messages/{id}
 * Delete a message (sender or receiver can delete).
 */
export const deleteMessage = async (id) => {
    try {
        await marketplaceClient.delete(`/messages/${id}`);
        return true;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to delete message');
    }
};

// ============================================
// Orders
// ============================================

/**
 * POST /orders
 * Place a new order. Backend validates stock, MOQ, and blocks self-purchase.
 */
export const createOrder = async (cartItems, shipping, paymentMethod = 'credit_card') => {
    try {
        const body = {
            items: cartItems.map((item) => ({
                component_id: item.componentId ?? item.id,
                quantity: item.quantity,
            })),
            shipping: {
                first_name: shipping.firstName,
                last_name:  shipping.lastName,
                email:      shipping.email,
                phone:      shipping.phone ?? '',
                address:    shipping.address,
                city:       shipping.city,
                state:      shipping.state,
                zip_code:   shipping.zipCode,
                country:    shipping.country ?? 'US',
            },
            payment_method: paymentMethod,
        };
        const response = await marketplaceClient.post('/orders', body);
        return response.data; // { order_id, status, total, created_at }
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Order failed');
    }
};

/**
 * GET /orders
 * Buyer's order history, newest first.
 */
export const getOrders = async () => {
    try {
        const response = await marketplaceClient.get('/orders');
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to load orders');
    }
};

/**
 * GET /orders/selling
 * Seller's incoming sales, newest first.
 */
export const getSellingOrders = async () => {
    try {
        const response = await marketplaceClient.get('/orders/selling');
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to load selling orders');
    }
};

/**
 * GET /orders/:orderId
 */
export const getOrder = async (orderId) => {
    try {
        const response = await marketplaceClient.get(`/orders/${orderId}`);
        return response.data;
    } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail || 'Order not found';
        const err = new Error(`${status}: ${detail}`);
        err.response = error.response; // preserve for retry logic
        throw err;
    }
};

/**
 * PATCH /orders/:orderId/status
 * Seller updates order status. Only sellers involved in the order can call this.
 */
export const updateOrderStatus = async (orderId, status, trackingNumber = null) => {
    try {
        const body = { status };
        if (trackingNumber) body.tracking_number = trackingNumber;
        const response = await marketplaceClient.patch(`/orders/${orderId}/status`, body);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to update order status');
    }
};

// ============================================
// Notifications
// ============================================

/**
 * GET /notifications
 */
export const getNotifications = async ({ unreadOnly = false, limit = 20 } = {}) => {
    try {
        const params = new URLSearchParams({ limit });
        if (unreadOnly) params.set('unread_only', 'true');
        const response = await marketplaceClient.get(`/notifications?${params}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to load notifications');
    }
};

/**
 * GET /notifications/unread-count
 */
export const getUnreadNotificationCount = async () => {
    try {
        const response = await marketplaceClient.get('/notifications/unread-count');
        return response.data.unread_count;
    } catch (error) {
        return 0; // Fail silently — bell just shows zero
    }
};

/**
 * PATCH /notifications/:id/read
 */
export const markNotificationRead = async (notificationId) => {
    try {
        await marketplaceClient.patch(`/notifications/${notificationId}/read`);
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to mark notification read');
    }
};

/**
 * POST /notifications/read-all
 */
export const markAllNotificationsRead = async () => {
    try {
        await marketplaceClient.post('/notifications/read-all');
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to mark all read');
    }
};

// ============================================
// Reviews
// ============================================

/**
 * POST /reviews
 * Create a review for a seller.
 * { seller_id, listing_id?, rating (1-5), review_text? }
 * One review per (reviewer + seller + listing).
 */
export const createReview = async (reviewData) => {
    try {
        const response = await marketplaceClient.post('/reviews', reviewData);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to create review');
    }
};

/**
 * GET /reviews
 * All reviews. Optional filters:
 * - seller_id: reviews for a specific seller
 */
export const getReviews = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.seller_id) params.append('seller_id', filters.seller_id);
        const response = await marketplaceClient.get(`/reviews?${params.toString()}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch reviews');
    }
};

/**
 * GET /reviews/seller/{id}/stats
 * Seller rating summary: average_rating, total_reviews, rating_distribution.
 */
export const getSellerReviewStats = async (sellerId) => {
    try {
        const response = await marketplaceClient.get(`/reviews/seller/${sellerId}/stats`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Failed to fetch seller review stats');
    }
};

// ============================================
// Default export (named + default)
// ============================================

const marketplaceService = {
    // Profile
    getMarketplaceProfile,

    // Components
    getComponents,
    searchComponents,
    getComponent,
    getMyComponents,
    createComponent,
    updateComponent,
    updateComponentQuantity,
    deleteComponent,
    getInventoryStats,

    // Images
    uploadImage,
    uploadImagesBatch,
    attachComponentImage,
    getComponentImages,
    deleteComponentImage,

    // Categories
    getCategories,

    // Saved / Favorites
    saveComponent,
    getSaved,
    removeSaved,

    // Messages
    sendMessage,
    getMessages,
    getInbox,
    getSentMessages,
    markMessageRead,
    deleteMessage,

    // Reviews
    createReview,
    getReviews,
    getSellerReviewStats,

    // Orders
    createOrder,
    getOrders,
    getSellingOrders,
    getOrder,
    updateOrderStatus,

    // Notifications
    getNotifications,
    getUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
};

export default marketplaceService;
